import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, XCircle, Users, Plus, Trash2, Settings2, HardDrive } from "lucide-react";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api } from "../auth/api";
import { useAuth } from "../auth/AuthContext";
import {
  PageHeader, Card, Stat, Badge, Button, Modal, Field, inputClass, Spinner, ErrorNote, Empty,
} from "../components/ui";

const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
const fmtDateTime = (iso) =>
  iso ? new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "Never";
const fmtSize = (b) => {
  if (!b) return "—";
  const u = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  while (b >= 1024 && i < u.length - 1) { b /= 1024; i++; }
  return `${b.toFixed(b >= 10 || i === 0 ? 0 : 1)} ${u[i]}`;
};

export default function BackupMonitor() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null); // cycle index
  const [manage, setManage] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await api("/backup/summary?cycles=6");
      setData(d);
      setSelected((s) => s ?? d.cycles[d.cycles.length - 1].index);
      setError("");
    } catch (e) {
      setError(e.message);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  if (!data) return error ? <ErrorNote>{error}</ErrorNote> : <Spinner label="Loading backup status" />;

  const cycle = data.cycles.find((c) => c.index === selected) || data.cycles[data.cycles.length - 1];
  const label = (c) => `${fmtDate(c.start)} – ${fmtDate(c.end)}`;
  const chart = data.cycles.map((c) => ({ name: fmtDate(c.start), Done: c.done, "Not done": c.notDone }));
  const pct = cycle.total ? Math.round((cycle.done / cycle.total) * 100) : 0;
  const pending = cycle.members.filter((m) => !m.done);
  const completed = cycle.members.filter((m) => m.done);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Backup Monitor"
        subtitle={`NAS backup status for each ${data.cycleDays === 14 ? "alternate-week" : `${data.cycleDays}-day`} cycle.`}
        actions={isAdmin && <Button variant="outline" onClick={() => setManage(true)}><Settings2 size={15} /> Manage</Button>}
      />

      <ErrorNote>{error}</ErrorNote>

      {/* Cycle picker */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {[...data.cycles].reverse().map((c) => (
          <button
            key={c.index}
            onClick={() => setSelected(c.index)}
            className={`shrink-0 px-3.5 py-2 rounded-lg border text-sm transition-colors ${
              c.index === cycle.index
                ? "bg-[#111111] text-white border-[#111111]"
                : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"
            }`}
          >
            {label(c)}
            {c.current && <span className="ml-2 text-[10px] uppercase tracking-wide text-[#ff8a8a]">Current</span>}
          </button>
        ))}
      </div>

      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-5">
        <Stat label="Tracked people" value={cycle.total} icon={<Users size={18} />} />
        <Stat label="Backup done" value={cycle.done} tone="good" icon={<CheckCircle2 size={18} />} />
        <Stat
          label={cycle.current ? "Not done yet" : "Not done"}
          value={cycle.notDone}
          tone={cycle.notDone ? "brand" : "default"}
          icon={<XCircle size={18} />}
        />
        <Stat label="Completion" value={`${pct}%`} hint={cycle.current ? "Cycle still in progress" : "Cycle closed"} />
      </div>

      <div className="grid xl:grid-cols-5 gap-5">
        <Card title="Last 6 cycles" className="xl:col-span-2">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chart} margin={{ left: -20, right: 4, top: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#9ca3af" />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="#9ca3af" />
                <Tooltip cursor={{ fill: "#f9fafb" }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Done" stackId="a" fill="#111111" radius={[0, 0, 0, 0]} />
                <Bar dataKey="Not done" stackId="a" fill="#C00000" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <p className="text-xs text-gray-400 mt-2">Each bar is one cycle, labelled by its start date.</p>
        </Card>

        <Card title={`People · ${label(cycle)}`} padded={false} className="xl:col-span-3">
          {cycle.members.length === 0 ? (
            <Empty>No one is being tracked yet.{isAdmin ? " Use Manage to add people." : ""}</Empty>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b border-gray-100">
                    <th className="px-5 py-3 font-medium">Name</th>
                    <th className="px-5 py-3 font-medium">Status</th>
                    <th className="px-5 py-3 font-medium">Last backup</th>
                    <th className="px-5 py-3 font-medium">Size</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {[...pending, ...completed].map((m) => (
                    <tr key={m.id} className="hover:bg-gray-50">
                      <td className="px-5 py-3">
                        <p className="font-medium text-[#111111]">{m.name}</p>
                        <p className="text-xs text-gray-500">{m.nasUsername}</p>
                      </td>
                      <td className="px-5 py-3">
                        {m.done ? <Badge tone="good">Done</Badge> : <Badge tone="bad">{cycle.current ? "Pending" : "Missed"}</Badge>}
                      </td>
                      <td className="px-5 py-3 text-gray-700 whitespace-nowrap">{fmtDateTime(m.lastBackupAt)}</td>
                      <td className="px-5 py-3 text-gray-700">{fmtSize(m.sizeBytes)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {manage && <ManageModal onClose={() => { setManage(false); load(); }} cycleDays={data.cycleDays} anchor={data.anchor} />}
    </div>
  );
}

function ManageModal({ onClose, cycleDays, anchor }) {
  const [targets, setTargets] = useState([]);
  const [name, setName] = useState("");
  const [nas, setNas] = useState("");
  const [days, setDays] = useState(cycleDays);
  const [anchorDate, setAnchorDate] = useState(anchor);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    try { setTargets(await api("/backup/targets")); } catch (e) { setError(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api("/backup/targets", { method: "POST", body: { name, nasUsername: nas } });
      setName(""); setNas("");
      load();
    } catch (err) { setError(err.message); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Stop tracking ${t.name}? Their backup history is deleted.`)) return;
    await api(`/backup/targets/${t.id}`, { method: "DELETE" });
    load();
  };

  const saveSettings = async () => {
    setError("");
    try {
      await api("/backup/settings", { method: "PUT", body: { cycleDays: Number(days), anchor: anchorDate } });
      setSaved(true);
    } catch (err) { setError(err.message); }
  };

  return (
    <Modal title="Manage backup tracking" onClose={onClose} width="max-w-xl">
      <div className="space-y-6">
        <ErrorNote>{error}</ErrorNote>

        <section>
          <h4 className="text-sm font-semibold text-[#111111] mb-2">Cycle</h4>
          <div className="grid sm:grid-cols-3 gap-3 items-end">
            <Field label="Backup due every">
              <select className={inputClass} value={days} onChange={(e) => { setDays(e.target.value); setSaved(false); }}>
                <option value={7}>1 week</option>
                <option value={14}>2 weeks (alternate)</option>
                <option value={28}>4 weeks</option>
              </select>
            </Field>
            <Field label="First cycle starts" hint="A Monday">
              <input type="date" className={inputClass} value={anchorDate} onChange={(e) => { setAnchorDate(e.target.value); setSaved(false); }} />
            </Field>
            <Button variant="outline" onClick={saveSettings}>{saved ? "Saved" : "Save"}</Button>
          </div>
        </section>

        <section>
          <h4 className="text-sm font-semibold text-[#111111] mb-2">People being tracked</h4>
          <form onSubmit={add} className="grid sm:grid-cols-[1fr_1fr_auto] gap-2 mb-3">
            <input className={inputClass} placeholder="Full name" value={name} onChange={(e) => setName(e.target.value)} required />
            <input className={inputClass} placeholder="NAS username / folder" value={nas} onChange={(e) => setNas(e.target.value)} required />
            <Button type="submit"><Plus size={15} /> Add</Button>
          </form>
          <p className="text-xs text-gray-400 mb-3">
            The NAS username must match the folder name under your backup share, e.g. <code>/Volume1/Backups/anil</code>.
          </p>
          {targets.length === 0 ? (
            <Empty>No one added yet.</Empty>
          ) : (
            <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
              {targets.map((t) => (
                <li key={t.id} className="flex items-center justify-between px-4 py-2.5">
                  <span className="flex items-center gap-2 text-sm">
                    <HardDrive size={14} className="text-gray-400" />
                    <span className="font-medium text-[#111111]">{t.name}</span>
                    <span className="text-gray-500">({t.nasUsername})</span>
                  </span>
                  <button onClick={() => remove(t)} className="text-gray-400 hover:text-[#C00000]" aria-label={`Remove ${t.name}`}>
                    <Trash2 size={15} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </Modal>
  );
}
